/**
 * Shared TypeScript types for the AI-Companion UI layer.
 *
 * Mirrors the row shape of `public.ai_suggestions` (see
 * supabase/migrations/20260517000006_ai_suggestions.sql). The payload
 * field is `unknown` here — per-`kind` components narrow it themselves
 * (see e.g. RefeedingSuggestionCard).
 */

export type AiSuggestionStatus =
  | 'pending'
  | 'shown'
  | 'accepted'
  | 'dismissed'
  | 'expired';

export type AiSuggestionPriority = 'low' | 'normal' | 'high' | 'critical';

export interface AiSuggestion {
  id: string;
  org_id: string;
  patient_id: string | null;
  kind: string;
  priority: AiSuggestionPriority;
  payload: unknown;
  source_run_id: string | null;
  dedup_key: string;
  status: AiSuggestionStatus;
  created_at: string;
  shown_at: string | null;
  expires_at: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  resolved_action: string | null;
}

/** Strukturierter Output der Refeeding-Capability (siehe
 *  supabase/functions/cap-assess-refeeding-risk/index.ts). */
export interface RefeedingSuggestionPayload {
  risk_level: 'low' | 'moderate' | 'high';
  deterministic_score: number;
  extreme_markers?: string[];
  factors: Array<{
    key: string;
    tier: 'major' | 'minor';
    label: string;
    evidence: string;
  }>;
  rationale: string;
  suggested_actions: Array<{ kind: string; label: string }>;
}

export function isRefeedingPayload(
  p: unknown,
): p is RefeedingSuggestionPayload {
  if (!p || typeof p !== 'object') return false;
  const o = p as Record<string, unknown>;
  return (
    typeof o.risk_level === 'string' &&
    typeof o.deterministic_score === 'number' &&
    Array.isArray(o.factors) &&
    typeof o.rationale === 'string' &&
    Array.isArray(o.suggested_actions)
  );
}

/** Output der Gewichts-Trend-Capability (siehe
 *  supabase/functions/cap-watch-weight-trend/index.ts). */
export interface WeightTrendSuggestionPayload {
  trend_level: 'stable' | 'moderate' | 'high';
  current_weight_kg: number;
  baseline_weight_kg: number | null;
  loss_pct_1m: number | null;
  loss_pct_6m: number | null;
  rationale: string;
  suggested_actions: Array<{ kind: string; label: string }>;
}

export function isWeightTrendPayload(
  p: unknown,
): p is WeightTrendSuggestionPayload {
  if (!p || typeof p !== 'object') return false;
  const o = p as Record<string, unknown>;
  return (
    typeof o.trend_level === 'string' &&
    typeof o.current_weight_kg === 'number' &&
    typeof o.rationale === 'string' &&
    Array.isArray(o.suggested_actions)
  );
}

/** Session-Briefing (cap-prepare-session-brief). */
export interface SessionBriefSuggestionPayload {
  patient_summary: string;
  open_items: string[];
  suggested_topics: Array<{ title: string; reason: string }>;
  red_flags: string[];
  suggested_actions: Array<{ kind: string; label: string }>;
}

export function isSessionBriefPayload(
  p: unknown,
): p is SessionBriefSuggestionPayload {
  if (!p || typeof p !== 'object') return false;
  const o = p as Record<string, unknown>;
  return (
    typeof o.patient_summary === 'string' &&
    Array.isArray(o.suggested_topics)
  );
}

/** Mangelernährungs-Risiko (cap-assess-malnutrition-risk). */
export interface MalnutritionRiskSuggestionPayload {
  risk_level: 'low' | 'moderate' | 'high';
  tool: string;
  deterministic_score: number;
  at_risk: boolean;
  rationale: string;
  suggested_actions: Array<{ kind: string; label: string }>;
}

export function isMalnutritionRiskPayload(
  p: unknown,
): p is MalnutritionRiskSuggestionPayload {
  if (!p || typeof p !== 'object') return false;
  const o = p as Record<string, unknown>;
  return (
    typeof o.risk_level === 'string' &&
    typeof o.tool === 'string' &&
    typeof o.deterministic_score === 'number' &&
    typeof o.rationale === 'string'
  );
}

/** Drop-Out-Risiko (scheduled-cap-drop-out). */
export interface DropOutSuggestionPayload {
  days_since_last_contact: number;
  last_appointment_at: string | null;
  next_appointment_at: string | null;
  rationale: string;
  suggested_actions: Array<{ kind: string; label: string }>;
}

export function isDropOutPayload(
  p: unknown,
): p is DropOutSuggestionPayload {
  if (!p || typeof p !== 'object') return false;
  const o = p as Record<string, unknown>;
  return (
    typeof o.days_since_last_contact === 'number' &&
    typeof o.rationale === 'string'
  );
}

/** Strategy-Health-Alert (scheduled-strategy-health). */
export interface StrategyHealthSuggestionPayload {
  capability_name: string;
  signal: 'reject_rate' | 'error_rate' | 'latency_drift' | 'cost_spike';
  current_value: number;
  threshold: number;
  total_runs_24h: number;
  rationale: string;
  recommendation: string;
  suggested_actions: Array<{ kind: string; label: string }>;
}

export function isStrategyHealthPayload(
  p: unknown,
): p is StrategyHealthSuggestionPayload {
  if (!p || typeof p !== 'object') return false;
  const o = p as Record<string, unknown>;
  return (
    typeof o.capability_name === 'string' &&
    typeof o.signal === 'string' &&
    typeof o.current_value === 'number' &&
    typeof o.threshold === 'number' &&
    typeof o.rationale === 'string' &&
    typeof o.recommendation === 'string'
  );
}

/** Pre-Visit-Intake-Dispatch (cap-dispatch-pre-visit-intake). */
export interface PreVisitIntakeSuggestionPayload {
  appointment_id: string;
  appointment_start_time: string;
  token: string;
  intake_url: string;
  expires_at: string;
  rationale: string;
  suggested_actions: Array<{ kind: string; label: string }>;
}

export function isPreVisitIntakePayload(
  p: unknown,
): p is PreVisitIntakeSuggestionPayload {
  if (!p || typeof p !== 'object') return false;
  const o = p as Record<string, unknown>;
  return (
    typeof o.appointment_id === 'string' &&
    typeof o.appointment_start_time === 'string' &&
    typeof o.token === 'string' &&
    typeof o.intake_url === 'string' &&
    typeof o.expires_at === 'string' &&
    typeof o.rationale === 'string'
  );
}

/** Morning Briefing (cap-prepare-morning-briefing). */
export interface MorningBriefingSuggestionPayload {
  greeting: string;
  headline_alerts: Array<{
    suggestion_id: string;
    title: string;
    patient_hint?: string;
  }>;
  headline_briefs: Array<{
    suggestion_id: string;
    patient_hint: string;
    one_liner: string;
  }>;
  appointment_count: number;
  patient_count: number;
  generated_at: string;
}

export function isMorningBriefingPayload(
  p: unknown,
): p is MorningBriefingSuggestionPayload {
  if (!p || typeof p !== 'object') return false;
  const o = p as Record<string, unknown>;
  return (
    typeof o.greeting === 'string' &&
    Array.isArray(o.headline_alerts) &&
    Array.isArray(o.headline_briefs) &&
    typeof o.appointment_count === 'number' &&
    typeof o.generated_at === 'string'
  );
}

/** GNCP-Vorschlag aus geschriebener Notiz (cap-suggest-gncp). */
export interface GncpDraftSuggestionPayload {
  assessment_summary: string;
  diagnosis_count: number;
  intervention_count: number;
  monitoring_count: number;
  rationale: string;
  source_note_id: string;
  suggested_actions: Array<{ kind: string; label: string }>;
}

export function isGncpDraftPayload(
  p: unknown,
): p is GncpDraftSuggestionPayload {
  if (!p || typeof p !== 'object') return false;
  const o = p as Record<string, unknown>;
  return (
    typeof o.assessment_summary === 'string' &&
    typeof o.diagnosis_count === 'number' &&
    typeof o.intervention_count === 'number' &&
    typeof o.rationale === 'string' &&
    typeof o.source_note_id === 'string'
  );
}
